/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving order forms.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialOrderFormState {
    orderForms: Dtos.OrderForm[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IOrderFormState {
    orderForms: IInitialOrderFormState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const OrderFormInitialState: IInitialOrderFormState = {
    orderForms: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useOrderFormsSelector: TypedUseSelectorHook<IOrderFormState> = useSelector;

export const OrderFormSelectors = {
    selectOrderForms: (state: IOrderFormState) => state.orderForms.orderForms,
    selectLoadState: (state: IOrderFormState) => state.orderForms.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class OrderFormReducer extends ImmerReducer<IInitialOrderFormState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(orderForms?: Dtos.OrderForm[]) {
        this.draftState.orderForms = orderForms ? orderForms : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.orderForms = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...OrderFormInitialState };
    }
};

export const OrderFormActions = createActionCreators(OrderFormReducer);
const reducer = createReducerFunction(OrderFormReducer, OrderFormInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const OrderFormApi = {
    load: () => {
        return client.get(new Dtos.GetOrderForms());
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const OrderFormLogic = {
    load: createLogic<IOrderFormState, {}, undefined, string, ReturnType<typeof OrderFormActions.load>>({
        type: OrderFormActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await OrderFormApi.load();

                dispatch(OrderFormActions.loadSuccess(
                    response.orderForms
                ));
            }
            catch (error: any) {
                dispatch(OrderFormActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    OrderFormLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useOrderForms = (): [Dtos.OrderForm[] | null, IRequestState<Dtos.ResponseStatus>, typeof OrderFormActions] => {
    const orderForms = useSelector(OrderFormSelectors.selectOrderForms);
    const loadState = useSelector(OrderFormSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof OrderFormActions>(() => {
        return bindActionCreators(OrderFormActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(OrderFormActions, dispatch));
    }, [dispatch, setActions]);

    return [
        orderForms,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('orderForms', reducer as any, logics as any);